import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import { formatApiError } from "@/lib/api";
import BrandLogo from "@/components/BrandLogo";
import { toast } from "sonner";
import { Lock } from "lucide-react";

export default function AdminLogin() {
    const { t } = useLang();
    const { user, login } = useAuth();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [busy, setBusy] = useState(false);
    const nav = useNavigate();

    if (user) return <Navigate to="/admin" replace />;

    const submit = async (e) => {
        e.preventDefault();
        setBusy(true);
        try {
            await login(email, password);
            nav("/admin", { replace: true });
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail, "Anmeldung fehlgeschlagen."));
        } finally { setBusy(false); }
    };

    return (
        <div className="min-h-screen grid lg:grid-cols-2 bg-brand-cream">
            <div className="relative hidden lg:flex flex-col justify-between p-12 bg-brand-green text-white overflow-hidden">
                <div className="absolute inset-0 grainy opacity-50" />
                <div className="relative">
                    <BrandLogo variant="light" />
                </div>
                <div className="relative">
                    <div className="eyebrow text-brand-yellow">Admin</div>
                    <h2 className="mt-3 font-display text-4xl font-bold leading-tight">Verwalte deinen Markt — frisch, fair, übersichtlich.</h2>
                    <p className="mt-3 text-white/70 max-w-md">Angebote, Kategorien, Bewerbungen und Newsletter — alles an einem Ort.</p>
                </div>
                <div className="relative text-xs text-white/50">© 2026 SCHOCK MARKT</div>
            </div>

            <div className="flex items-center justify-center p-6 md:p-12">
                <form onSubmit={submit} className="w-full max-w-md" data-testid="admin-login-form">
                    <div className="lg:hidden mb-8"><BrandLogo /></div>
                    <div className="inline-flex items-center gap-2 rounded-full bg-brand-green/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-widest text-brand-green">
                        <Lock className="h-3 w-3" /> {t.admin.login}
                    </div>
                    <h1 className="mt-4 font-display text-3xl md:text-4xl font-bold">Willkommen zurück.</h1>
                    <p className="mt-2 text-sm text-brand-charcoal/65">Melde dich an, um den Markt zu verwalten.</p>

                    <div className="mt-8">
                        <label className="block text-xs font-semibold uppercase tracking-widest text-brand-charcoal/60 mb-2">{t.admin.email}</label>
                        <input
                            data-testid="admin-email"
                            type="email"
                            required
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="input-clean"
                            placeholder="admin@schockmarkt.com"
                        />
                    </div>
                    <div className="mt-4">
                        <label className="block text-xs font-semibold uppercase tracking-widest text-brand-charcoal/60 mb-2">{t.admin.password}</label>
                        <input
                            data-testid="admin-password"
                            type="password"
                            required
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="input-clean"
                            placeholder="••••••••"
                        />
                    </div>

                    <button disabled={busy} type="submit" data-testid="admin-login-submit" className="mt-8 btn-primary w-full justify-center disabled:opacity-50">
                        {busy ? "..." : t.admin.signIn}
                    </button>
                </form>
            </div>
        </div>
    );
}
